import { Config } from "@modules/config"
import * as Sentry from "@sentry/react"
import { type ZodType, ZodError } from "zod"

const MAX_RAW_DATA_LENGTH = 1000

function truncateRawData(data: unknown): string {
  try {
    const str = typeof data === "string" ? data : JSON.stringify(data ?? null)
    if (str.length <= MAX_RAW_DATA_LENGTH) return str
    return str.slice(0, MAX_RAW_DATA_LENGTH) + "...[truncated]"
  } catch {
    return "[unable to serialize]"
  }
}

export class LoggerService {
  static parseApiDto<T>(schema: ZodType<T>, data: unknown, schemaName?: string): T {
    const name = schemaName ?? schema.meta()?.id ?? schemaName ?? "UnknownSchema"
    const result = schema.safeParse(data)
    if (!result.success) {
      LoggerService.captureException(name, result.error, data)
      throw result.error
    }
    return result.data
  }

  static captureException(name: string, error: unknown, rawData?: unknown) {
    if (Config.ENV === "dev") {
      console.error("[SENTRY Exception] " + name, error)
    }

    if (error instanceof ZodError) {
      const zodContext: Record<string, unknown> = {
        schemaName: name,
        issues: error.issues.map((issue) => ({
          path: issue.path.join("."),
          code: issue.code,
          message: issue.message,
          expected: "expected" in issue ? issue.expected : undefined,
          received: "received" in issue ? issue.received : undefined,
        })),
      }

      if (rawData !== undefined) {
        zodContext.rawData = truncateRawData(rawData)
      }

      Sentry.captureException(error, {
        contexts: {
          zod: zodContext,
        },
      })

      return
    }

    if (error instanceof Error) {
      error.name = `${name} - ${error.name}`

      Sentry.captureException(error, {
        contexts: {
          Error: {
            Message: error.message,
          },
        },
      })

      return
    }
    Sentry.captureException(error)
  }
}
